import type { FC, ReactNode } from 'react'

import { SPARKLE_PATH } from '@/components/landing/decor/sparkle'

interface WorldMapProps {
    className?: string
    // The scroll-drawn map in the chapter scene animates its lines; a static copy shows them drawn.
    animated?: boolean
}

type MarkerKind = 'hq' | 'ruin' | 'tower' | 'dorm' | 'unknown'

interface MapMarker {
    x: number
    y: number
    code: string
    name: string
    dx: number
    dy: number
    kind: MarkerKind
}

const COAST = 'M60 300C76 168 196 82 356 90C516 98 640 150 720 238C792 318 762 458 662 520C562 582 382 562 262 522C142 482 50 424 60 300Z'

const CONTOURS = [
    'M150 262C150 182 222 140 292 150C372 162 402 222 392 282C382 352 312 382 242 372C172 362 150 322 150 262Z',
    'M182 264C182 202 234 172 288 180C348 190 370 234 362 280C354 332 302 354 250 346C198 338 182 308 182 264Z',
    'M452 362C457 292 522 262 592 272C662 282 692 332 682 392C672 452 602 472 542 462C482 452 448 422 452 362Z',
    'M487 364C490 314 536 294 586 302C636 310 656 344 648 388C640 432 592 446 548 438C504 430 484 406 487 364Z'
]

const RIVER = 'M380 96C368 162 410 210 420 262C430 322 398 380 430 440C452 490 500 520 522 560'

// Old service roads between the remaining buildings.
const ROADS = 'M200 400L290 290L440 330L600 420M290 290L360 470L520 500L600 420M440 330L520 250L650 320'

// Labels for what is left of the facility. They name the ruins as they look on the map, not
// what each building was for: the site's past stays unread.
const MARKERS: Array<MapMarker> = [
    { x: 290, y: 290, code: 'HQ', name: 'マホニア観測局', dx: -110, dy: -90, kind: 'hq' },
    { x: 440, y: 330, code: 'LAB-02', name: '旧研究棟', dx: 50, dy: -50, kind: 'ruin' },
    { x: 200, y: 400, code: 'ARCHIVE', name: '記録保管庫', dx: -60, dy: 50, kind: 'ruin' },
    { x: 600, y: 420, code: 'COMM', name: '通信設備跡', dx: 60, dy: 30, kind: 'ruin' },
    { x: 360, y: 470, code: 'TOWER', name: '観測塔跡', dx: -40, dy: 60, kind: 'tower' },
    { x: 520, y: 500, code: 'MAINT', name: '整備区画', dx: 50, dy: 30, kind: 'ruin' },
    { x: 520, y: 250, code: 'QUARTERS', name: '居住区画', dx: 60, dy: 20, kind: 'ruin' },
    { x: 650, y: 320, code: 'DORM', name: '局員寮', dx: 40, dy: -30, kind: 'dorm' },
    { x: 160, y: 200, code: 'UNKNOWN', name: '未確認区画', dx: 30, dy: -40, kind: 'unknown' }
]

const GRID_X = [100, 200, 300, 400, 500, 600, 700]
const GRID_Y = [100, 200, 300, 400, 500]

const LEADER = 70
const GOLD = '#e2b33c'
const GOLD_DEEP = '#a8791a'

const Footprint: FC<{ marker: MapMarker }> = ({ marker }): ReactNode => {
    const { x, y, kind } = marker

    if (kind === 'hq') {
        return (
            <g>
                <rect x={x - 36} y={y - 24} width='72' height='48' fill={GOLD} fillOpacity='0.12' stroke={GOLD_DEEP} strokeOpacity='0.55' />
                <rect x={x + 40} y={y - 10} width='22' height='30' stroke='currentColor' strokeOpacity='0.35' strokeDasharray='5 4' />
            </g>
        )
    }

    if (kind === 'tower') return <circle cx={x} cy={y} r='20' stroke='currentColor' strokeOpacity='0.35' strokeDasharray='5 4' />
    if (kind === 'unknown') return <circle cx={x} cy={y} r='24' stroke='currentColor' strokeOpacity='0.3' strokeDasharray='2 5' />
    if (kind === 'dorm') return <rect x={x - 22} y={y - 16} width='44' height='32' rx='3' fill='#fbf3dc' fillOpacity='0.7' stroke={GOLD_DEEP} strokeOpacity='0.45' />

    return <rect x={x - 26} y={y - 17} width='52' height='34' stroke='currentColor' strokeOpacity='0.35' strokeDasharray='5 4' />
}

const MarkerDot: FC<{ marker: MapMarker }> = ({ marker }): ReactNode => {
    const { x, y, kind } = marker

    if (kind === 'unknown') {
        return (
            <>
                <circle cx={x} cy={y} r='9' fill='#fbfaf6' stroke='currentColor' strokeOpacity='0.5' strokeDasharray='2 3' />
                <text x={x} y={y + 4} textAnchor='middle' fill='currentColor' fillOpacity='0.6' fontSize='11' className='font-mono'>?</text>
            </>
        )
    }

    const size = kind === 'hq' ? 1 : 0.75
    const half = 12 * size

    return (
        <>
            {kind === 'hq' && <circle cx={x} cy={y} r='19' stroke={GOLD_DEEP} strokeOpacity='0.5' strokeDasharray='2 4' />}
            <circle cx={x} cy={y} r={kind === 'hq' ? 13 : 11} fill='#fbfaf6' stroke={GOLD_DEEP} strokeOpacity='0.75' />
            <path d={SPARKLE_PATH} fill={GOLD} transform={`translate(${x - half} ${y - half}) scale(${size})`} />
        </>
    )
}

// Site map of the old facility the observatory now lives in: terrain contours, the remaining
// buildings as dashed footprints, service roads and labelled points. In the chapter scene,
// lines marked data-map-draw are drawn in on scroll and [data-map-marker] points appear in turn.
const WorldMap: FC<WorldMapProps> = ({ className, animated = true }): ReactNode => {
    const draw = animated ? { 'data-map-draw': true } : {}

    return (
        <svg viewBox='0 0 800 600' fill='none' aria-hidden='true' focusable='false' className={className}>
            <g data-map-grid stroke='currentColor' strokeOpacity='0.12'>
                {GRID_X.map(x => <line key={`x${x}`} x1={x} y1='40' x2={x} y2='560' />)}
                {GRID_Y.map(y => <line key={`y${y}`} x1='40' y1={y} x2='760' y2={y} />)}
            </g>

            <g data-map-grid fill='currentColor' fillOpacity='0.5' fontSize='10' letterSpacing='2' className='font-mono'>
                <text x='104' y='34'>SECTOR 10.05</text>
                <text x='504' y='34'>SECTOR 10.40</text>
                <text x='8' y='196'>E-01</text>
                <text x='8' y='496'>E-02</text>
            </g>

            <path {...draw} pathLength={1} d={COAST} stroke='currentColor' strokeOpacity='0.4' strokeWidth='1.4' />

            {CONTOURS.map((path, index) => (
                <path
                    key={path}
                    {...draw}
                    pathLength={1}
                    d={path}
                    stroke='currentColor'
                    strokeOpacity={0.14 + (index % 2) * 0.06}
                    strokeWidth='1'
                />
            ))}

            <path {...draw} pathLength={1} d={RIVER} stroke='#4d8dbf' strokeOpacity='0.5' strokeWidth='2' strokeLinecap='round' />

            <path data-map-grid d={ROADS} stroke='currentColor' strokeOpacity='0.32' strokeWidth='1.2' strokeDasharray='2 6' strokeLinecap='round' />

            <g data-map-grid>
                {MARKERS.map(marker => <Footprint key={`fp-${marker.code}`} marker={marker} />)}
            </g>

            {MARKERS.map(marker => {
                const right = marker.dx > 0
                const elbowX = marker.x + marker.dx * 0.55
                const elbowY = marker.y + marker.dy * 0.55
                const textX = right ? elbowX + 4 : elbowX - 4
                const anchor = right ? 'start' : 'end'
                const hq = marker.kind === 'hq'

                return (
                    <g key={marker.code} data-map-marker={animated ? true : undefined}>
                        <path
                            d={`M${marker.x} ${marker.y}L${elbowX} ${elbowY}h${right ? LEADER : -LEADER}`}
                            stroke='currentColor'
                            strokeOpacity='0.4'
                        />
                        <MarkerDot marker={marker} />
                        <text
                            x={textX}
                            y={elbowY - 8}
                            textAnchor={anchor}
                            fill={hq ? GOLD_DEEP : 'currentColor'}
                            fillOpacity={hq ? 1 : 0.7}
                            fontSize='10'
                            letterSpacing='2'
                            className='font-mono'
                        >
                            {marker.code}
                        </text>
                        <text
                            x={textX}
                            y={elbowY + 16}
                            textAnchor={anchor}
                            fill='currentColor'
                            fillOpacity={marker.kind === 'unknown' ? 0.6 : 1}
                            fontSize={hq ? 15 : 13}
                            className='font-sans'
                        >
                            {marker.name}
                        </text>
                    </g>
                )
            })}
        </svg>
    )
}

export default WorldMap
